import { socialLinks } from "./contact";

export const siteSeo = {
  siteName: "Pawwash",
  siteUrl: process.env.REACT_APP_SITE_URL || "https://pawwash.co.uk",
  defaultOgImage: "/og-image.jpg",
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogImageAlt: "Pawwash self-serve dog wash stations in Glasgow and Scotland",
  logoPath: "/logo192.png",
  twitterHandle: "@pawwash_uk",
  locale: "en_GB",
};

export const storeCtas = {
  discovery: { label: "Find your nearest station", href: "/" },
  scroll: { label: "Find a store near me", target: "#store-locator" },
};

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: siteSeo.siteName,
  url: siteSeo.siteUrl,
  logo: `${siteSeo.siteUrl}${siteSeo.logoPath}`,
  sameAs: socialLinks.map((link) => link.url.trim()),
  contactPoint: {
    "@type": "ContactPoint",
    email: "support@pawwash.co.uk",
    contactType: "customer support",
    areaServed: "GB",
    availableLanguage: "English",
  },
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: siteSeo.siteName,
  url: siteSeo.siteUrl,
  potentialAction: {
    "@type": "FindAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${siteSeo.siteUrl}/#store-locator`,
    },
    description: "Find your nearest Pawwash self-serve dog wash station",
  },
};

export const buildFaqSchema = (faqItems) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqItems.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: {
      "@type": "Answer",
      text: answer,
    },
  })),
});

export const buildHowToSchema = (steps) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to use a Pawwash self-serve dog wash station",
  description:
    "Step-by-step guide to washing your dog at a Pawwash station. £10 for 10 minutes including shampoo, rinse, conditioner, blow dry, and tub disinfect.",
  totalTime: "PT10M",
  estimatedCost: {
    "@type": "MonetaryAmount",
    currency: "GBP",
    value: "10",
  },
  step: steps.map((step, index) => ({
    "@type": "HowToStep",
    position: index + 1,
    name: step.title,
    text: step.description,
    ...(step.duration ? { timeRequired: step.duration } : {}),
  })),
});

const formatOpeningHours = (store) => {
  if (store.is_24) {
    return "Mo-Su 00:00-24:00";
  }
  if (store.open_time && store.close_time) {
    return `Mo-Su ${store.open_time}-${store.close_time}`;
  }
  return undefined;
};

export const buildStoreListSchema = (stores) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Pawwash dog wash stations in Scotland",
  numberOfItems: stores.length,
  itemListElement: stores.map((store, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@type": "LocalBusiness",
      name: `Pawwash at ${store.name}`,
      description: "Self-serve dog wash station at Speed Queen laundry",
      url: siteSeo.siteUrl,
      address: {
        "@type": "PostalAddress",
        streetAddress: store.address,
        postalCode: store.post_code,
        addressCountry: "GB",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: Number(store.latitude),
        longitude: Number(store.longitude),
      },
      ...(formatOpeningHours(store)
        ? { openingHours: formatOpeningHours(store) }
        : {}),
    },
  })),
});

export const buildBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: `${siteSeo.siteUrl}${item.path}`,
  })),
});

export const innerPageBreadcrumbs = {
  demo: [
    { name: "Home", path: "/" },
    { name: "How it works", path: "/demo" },
  ],
  pricing: [
    { name: "Home", path: "/" },
    { name: "Pricing", path: "/pricing" },
  ],
  faq: [
    { name: "Home", path: "/" },
    { name: "FAQ", path: "/faq" },
  ],
  about: [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
  ],
  contact: [
    { name: "Home", path: "/" },
    { name: "Contact", path: "/contact" },
  ],
};
